import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import { isMockUsed } from '@nvision/services/mock';
import { Spin } from 'antd';

import { AuthCallback } from '@nvision/identity-core';

import { OIDC_AUTH_ROUTES, OIDCAuthReset } from '@/modules/auth';
import { routes as contractsRoutes } from '@/modules/contracts-management';
import { routes as pricingRoutes } from '@/modules/pricing';
import { routes as procurementRoutes } from '@/modules/procurement';

import { APP_ROUTES } from '../../contants/routes';
import { changeLanguageAndRoute } from '../../i18n';
import { checkNXPUserEffect } from '../../redux/RoutingGuide/RoutingGuideEffects';
import { applyTheme } from '../../utilz';
import { routeFileSelector } from '../../utilz/i18n';
import ClaimsLayout from '../layouts/ClaimsLayout';
import QuoteDetails from '../pages/Quote/QuoteDetails';
import SaversHome from '../pages/Savers/Home';
import Locations from '../pages/Savers/Locations';
import SaverShipment from '../pages/Savers/SaversShipment';
import SupplyZone from '../pages/Savers/SupplyZone';
import TrailerDatabase from '../pages/Savers/TrailerDatabase';
import TrailerMainPage from '../pages/Savers/TrailerManagement/TrailerMainPage';
import TrailerRequest from '../pages/Savers/TrailerRequests/TrailerRequest';
import TrailerReview from '../pages/Savers/TrailerReview';
import GetQuotePage from '../pages/Shipment/GetQuotePage';
import LossAndDamageCreateClaimScreen from '../screens/Claims/LossAndDamage/Create/LossAndDamageCreateClaimScreen';
import CreateOverChargeClaimsScreen from '../screens/Claims/OverCharge/CreateOverChargeClaimsScreen';
import { Protected } from './protected';

const AppLayouts = React.lazy(() => import('../layouts/AppLayouts'));
const Home = React.lazy(() => import('../pages/Home/Home'));
const PageNotFound = React.lazy(() => import('../pages/PageNotFound'));
const CreateShipmentPage = React.lazy(() => import('../pages/PurchaseOrder/CreateShipmentPage'));
const PurchaseOrderPage = React.lazy(() => import('../pages/PurchaseOrder/PurchaseOrderPage'));
const DashboardPage = React.lazy(() => import('../pages/Dashboard/DashboardPage'));
const CarrierDashboardPage = React.lazy(() => import('../pages/CarrierDashboard/CarrierDashboardPage'));
const CarrierRegistrationPage = React.lazy(() => import('../pages/CarrierRegistration/CarrierRegistrationPage'));
const SearchQuotePage = React.lazy(() => import('../pages/Shipment/SearchQuotePage'));
const QuoteView = React.lazy(() => import('../screens/Shipment/components/QuoteCard/EditQuoteDrawer/EditQuoteDrawer'));
const QuoteViewHistory = React.lazy(
  () => import('../screens/Shipment/components/QuoteCard/EditQuoteDrawer/QuoteHistoryDrawer'),
);
const QuoteCreate = React.lazy(() => import('../../views/screens/Shipment/CreateQuoteDrawer'));
// biome-ignore lint/style/noRestrictedImports: TODO: investigate
const QuoteGetRate = React.lazy(() => import('@/modules/spot-quote/components/quote-get-rate/quote-get-rate-modal'));
const VendorParcelExpress = React.lazy(() => import('../pages/VendorParcelExpress/VendorParcelExpress'));
const AddShipmentPage = React.lazy(() => import('../pages/Shipment/AddShipmentPage'));
const TmsSettingsPage = React.lazy(() => import('../pages/Settings/TmsSettingsPage'));
const AuctionPage = React.lazy(() => import('../pages/Auction/AuctionPage'));
const VisibilityPage = React.lazy(() => import('../pages/Visibility/VisibilityPage'));
const ShipmentLayout = React.lazy(() => import('../layouts/ShipmentLayout'));
const PurchaseOrderLayout = React.lazy(() => import('../layouts/PurchaseOrderLayout'));

const RoutingGuidePage = React.lazy(() => import('../pages/RoutingGuide/RoutingGuidePage'));

const NXPRoutingGuidePage = React.lazy(() => import('../pages/NXPRoutingGuide/NXPRoutingGuidePage'));

const ProcurementPage = React.lazy(() => import('../pages/Procurement/ProcurementPage'));
const RateShopPage = React.lazy(() => import('../pages/RateShop/RateShopPage'));
const AddProjectProcurement = React.lazy(() => import('../pages/Procurement/AddProjectProcurement'));
const AddTenderProcurement = React.lazy(() => import('../pages/Procurement/AddTenderProcurement'));
const AddRoutingGuide = React.lazy(() => import('../pages/RoutingGuide/AddRoutingGuide'));
const AddNxpUser = React.lazy(() => import('../pages/NXPRoutingGuide/NxpUserPage'));
const ProcurementRouteConfigurationPage = React.lazy(
  () => import('../pages/Procurement/ProcurementRouteConfigurationPage'),
);

const HalRoutingGuidePage = React.lazy(() => import('../pages/HalRoutingGuide/HalRoutingGuidePage'));

const SaversLayout = React.lazy(() => import('../screens/Savers/SaversTabs'));

const ErrorPage = React.lazy(() => import('../pages/ErrorPage'));

const LazyTranslatedPage = ({ element: Element }) => {
  return (
    <React.Suspense
      fallback={<Spin size="large" className="h-full w-full flex-1 d-flex justify-content-center align-items-center" />}
    >
      <Element />
    </React.Suspense>
  );
};

const AppRoutes = () => {
  const userData = useSelector((state) => state.auth.user.data, shallowEqual);
  const getUserPreference = useSelector((state) => state.auth.getUserPreference, shallowEqual);
  const { i18n } = useTranslation();
  const userThemesData = useSelector((state) => state.settings.userThemes.data.shallowEqual);
  const location = useLocation();
  const currentPath = location.pathname;
  const [initialized, setInitialized] = useState(false);
  // biome-ignore lint/complexity/noUselessEscapeInRegex: TODO: check
  const _lng = i18n.language?.replace(/\-.*/g, '');
  const _transformedPath = useMemo(() => routeFileSelector(currentPath), [currentPath]);

  const [nxpUser, setNxpUser] = useState(() => {
    const storedData = localStorage.getItem('npxUser');
    return storedData ? JSON.parse(storedData) : {};
  });

  const isLoadingNPXUserRef = useRef(false);

  useEffect(() => {
    const checkUser = async () => {
      if (!isLoadingNPXUserRef.current && !localStorage.getItem('npxUser')) {
        try {
          isLoadingNPXUserRef.current = true;
          const { data } = await checkNXPUserEffect();
          localStorage.setItem('npxUser', JSON.stringify(data));
          setNxpUser(data);
        } catch (error) {
          console.error('Error checking NXP user:', error);
        } finally {
          isLoadingNPXUserRef.current = false;
        }
      }
    };

    if (userData) {
      checkUser();
    }
  }, [userData]);

  useEffect(() => {
    const initI18n = async () => {
      const storedLanguage = localStorage.getItem('i18nextLng');
      const language = storedLanguage || 'en';
      const namespace = 'translation';

      await changeLanguageAndRoute(language, namespace);
      setInitialized(true);
    };

    if (!initialized) {
      initI18n();
    }
  }, [initialized]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: refactor after new auth flow implementation
  useEffect(() => {
    if (getUserPreference?.success && getUserPreference?.data) {
      i18n.changeLanguage(getUserPreference?.data?.preferedLanguage);
      applyTheme({
        ...userThemesData,
        ...(getUserPreference?.data ? getUserPreference?.data : {}),
      });
    }
  }, [getUserPreference?.success, userData]);

  const renderRoutes = (routes) => {
    return routes.map((route, index) => {
      const { Component, path, routes: childRoutes, ...rest } = route;

      return (
        <Route key={path || index} path={path} element={<Component />} {...rest}>
          {childRoutes && childRoutes.length > 0 && renderRoutes(childRoutes)}
        </Route>
      );
    });
  };

  return (
    <Routes>
      <Route path={OIDC_AUTH_ROUTES.OIDC_AUTH_CALLBACK} element={<AuthCallback />} />
      <Route path={OIDC_AUTH_ROUTES.OIDC_AUTH_RESET} element={<OIDCAuthReset />} />
      <Route
        path={APP_ROUTES.HOME}
        element={
          <Protected>
            <AppLayouts />
          </Protected>
        }
      >
        {isMockUsed() && (
          <Route path={pricingRoutes.root.path} element={<pricingRoutes.root.Component />}>
            {renderRoutes(pricingRoutes.routes)}
          </Route>
        )}

        {isMockUsed() && (
          <Route path={contractsRoutes.root.path} element={<contractsRoutes.root.Component />}>
            {renderRoutes(contractsRoutes.routes)}
          </Route>
        )}

        {isMockUsed() && (
          <Route path={procurementRoutes.root.path} element={<procurementRoutes.root.Component />}>
            {renderRoutes(procurementRoutes.routes)}
          </Route>
        )}

        <Route path={APP_ROUTES.HOME} element={<Home />} />
        <Route path={APP_ROUTES.DASHBOARD} element={<DashboardPage />} />
        <Route path={APP_ROUTES.CarrQuoteDashboard} element={<CarrierDashboardPage />} />
        <Route path={APP_ROUTES.CARRIER_REGISTRATION} element={<CarrierRegistrationPage />} />
        <Route path={APP_ROUTES.SEARCH_QUOTE} element={<LazyTranslatedPage element={SearchQuotePage} />}>
          <Route path={APP_ROUTES.SEARCH_QUOTE_ID} element={<LazyTranslatedPage element={QuoteView} />}>
            <Route
              path={APP_ROUTES.SEARCH_QUOTE_ID_HISTORY}
              element={<LazyTranslatedPage element={QuoteViewHistory} />}
            />
          </Route>
          <Route path={APP_ROUTES.SEARCH_QUOTE_CREATE} element={<LazyTranslatedPage element={QuoteCreate} />} />
          <Route path={APP_ROUTES.SEARCH_QUOTE_GET_RATE} element={<LazyTranslatedPage element={QuoteGetRate} />} />
        </Route>

        <Route
          exact
          path={APP_ROUTES.ADD_SHIPMENT}
          element={<LazyTranslatedPage element={AddShipmentPage} path={APP_ROUTES.ADD_SHIPMENT} />}
        />
        {/* Shipment Tab Routes Starts */}
        <Route path={APP_ROUTES.SHIPMENT} element={<LazyTranslatedPage element={ShipmentLayout} />}>
          <Route exact path={APP_ROUTES.SHIPMENT} />
          <Route exact path={`${APP_ROUTES.SHIPMENT_GET_QUOTE}/:shipmentKey?`} element={<LazyTranslatedPage />} />
          <Route
            exact
            path={`${APP_ROUTES.SHIPMENT_PARCEL_EXPRESS}/:shipmentNumber?`}
            element={<LazyTranslatedPage />}
          />
          <Route exact path={APP_ROUTES.SHIPMENT_GET_QUOTE} />
          <Route exact path={APP_ROUTES.SHIPMENT_NOTIFICATION_ROUTING} element={<LazyTranslatedPage />} />
          <Route path="*" element={<LazyTranslatedPage element={PageNotFound} />} />
        </Route>
        <Route
          exact
          path={APP_ROUTES.SHIPMENT_GET_QUOTE_FOR_MAIN}
          element={<LazyTranslatedPage element={GetQuotePage} path={APP_ROUTES.SHIPMENT_GET_QUOTE_FOR_MAIN} />}
        />
        {/* Shipment Tab Routes Ends*/}

        {/* Savers Tab Routes Start*/}
        <Route path={APP_ROUTES.SAVERS} element={<LazyTranslatedPage element={SaversLayout} path="/savers" />}>
          <Route exact path={APP_ROUTES.SAVERS} />
          <Route exact path={APP_ROUTES.SAVERS_HOME} element={<LazyTranslatedPage element={SaversHome} />} />
          <Route exact path={APP_ROUTES.SAVERS_SUPPLYZONE} element={<LazyTranslatedPage element={SupplyZone} />} />
          <Route exact path={APP_ROUTES.SAVERS_LOCATIONS} element={<LazyTranslatedPage element={Locations} />} />
          <Route exact path={APP_ROUTES.SAVERS_SHIPMENT} element={<LazyTranslatedPage element={SaverShipment} />} />
          <Route
            exact
            path={APP_ROUTES.SAVERS_TRAILERDATABASE}
            element={<LazyTranslatedPage element={TrailerDatabase} />}
          />
        </Route>

        {/* Savers Tab Routes Ends*/}

        {/* Purchase Order Tab Routes Starts */}
        <Route
          path={APP_ROUTES.PURCHASE_ORDER_MANAGEMENT}
          element={<LazyTranslatedPage element={PurchaseOrderLayout} />}
        >
          <Route exact path={APP_ROUTES.PURCHASE_ORDER_MANAGEMENT} element={<LazyTranslatedPage />} />
          <Route exact path={APP_ROUTES.PURCHASE_ORDER_DRAFT_SEARCH} element={<LazyTranslatedPage />} />
          <Route exact path={APP_ROUTES.PURCHASE_ORDER_PRE_BOOKING_SEARCH} element={<LazyTranslatedPage />} />

          <Route exact path={APP_ROUTES.PURCHASE_ORDER_SHIPMENT_SEARCH} element={<LazyTranslatedPage />} />
          <Route exact path={APP_ROUTES.PURCHASE_ORDER_VENDOR_SEARCH} element={<LazyTranslatedPage />} />
          <Route exact path={APP_ROUTES.PURCHASE_ORDER_ROUTING_GUIDE} element={<LazyTranslatedPage />} />
          <Route path="*" element={<LazyTranslatedPage element={PageNotFound} />} />
        </Route>
        {/* Purchase Order Tab Routes Ends */}

        <Route
          exact
          path={APP_ROUTES.PURCHASE_ORDER_PARCEL_EXPRESS}
          element={<LazyTranslatedPage element={VendorParcelExpress} />}
        />

        <Route
          exact
          path={APP_ROUTES.PURCHASE_ORDER_VIEW}
          element={<LazyTranslatedPage element={PurchaseOrderPage} />}
        />
        <Route
          exact
          path={APP_ROUTES.PURCHASE_ORDER_MANAGEMENT_ID}
          element={<LazyTranslatedPage element={PurchaseOrderPage} />}
        />
        <Route exact path={APP_ROUTES.CREATE_SHIPMENT} element={<LazyTranslatedPage element={CreateShipmentPage} />} />
        <Route
          exact
          path={APP_ROUTES.CREATE_SHIPMENT_ID}
          element={<LazyTranslatedPage element={CreateShipmentPage} />}
        />

        <Route exact path={APP_ROUTES.TMS_SETTINGS} element={<LazyTranslatedPage element={TmsSettingsPage} />} />
        <Route exact path={APP_ROUTES.AUCTION} element={<LazyTranslatedPage element={AuctionPage} />} />
        <Route exact path={APP_ROUTES.VISIBILITY} element={<LazyTranslatedPage element={VisibilityPage} />} />
        <Route
          exact
          path={APP_ROUTES.ADD_PROJECT_PROCUREMENT}
          element={<LazyTranslatedPage element={AddProjectProcurement} />}
        />
        <Route
          exact
          path={APP_ROUTES.ADD_TENDER_PROCUREMENT}
          element={<LazyTranslatedPage element={AddTenderProcurement} />}
        />
        <Route exact path={APP_ROUTES.ADD_ROUTING_GUIDE} element={<LazyTranslatedPage element={AddRoutingGuide} />} />
        <Route exact path={APP_ROUTES.ADD_NXP_USER} element={<LazyTranslatedPage element={AddNxpUser} />} />
        {nxpUser?.status === 0 && (
          <Route exact path={APP_ROUTES.ROUTING_GUIDE} element={<LazyTranslatedPage element={RoutingGuidePage} />} />
        )}
        {nxpUser?.status === 1 && (
          <Route exact path={APP_ROUTES.ROUTING_GUIDE} element={<LazyTranslatedPage element={NXPRoutingGuidePage} />}>
            <Route
              exact
              path={APP_ROUTES.NXP_ROUTING_GUIDE}
              element={<LazyTranslatedPage element={NXPRoutingGuidePage} />}
            />
            <Route exact path={APP_ROUTES.ROUTING_GUIDE_USER} element={NXPRoutingGuidePage} />
            <Route
              exact
              path={APP_ROUTES.ROUTING_GUIDE}
              element={<LazyTranslatedPage element={NXPRoutingGuidePage} />}
            />

            <Route path="*" element={<LazyTranslatedPage element={PageNotFound} />} />
          </Route>
        )}

        <Route exact path={APP_ROUTES.PROCUREMENT} element={<LazyTranslatedPage element={ProcurementPage} />}>
          <Route
            exact
            path={APP_ROUTES.PROCUREMENT_PROJECT}
            element={<LazyTranslatedPage element={ProcurementPage} />}
          />
          <Route exact path={APP_ROUTES.PROCUREMENT_TENDER} />
          <Route exact path={APP_ROUTES.PROCUREMENT_BID_ANALYSIS} element={<LazyTranslatedPage />} />
          <Route
            exact
            path={APP_ROUTES.PROCUREMENT_ROUTE_CONFIG}
            element={<LazyTranslatedPage element={ProcurementRouteConfigurationPage} />}
          >
            <Route exact path={APP_ROUTES.PROCUREMENT_ROUTE_CONFIG} element={<LazyTranslatedPage />} />
          </Route>
          <Route path="*" element={<LazyTranslatedPage element={PageNotFound} />} />
        </Route>
        <Route path={APP_ROUTES.RATE_SHOP} element={<LazyTranslatedPage element={RateShopPage} />} />

        {/* Claims Tab Routes Starts */}
        <Route path="/claims" element={<LazyTranslatedPage element={ClaimsLayout} />}>
          <Route exact path={APP_ROUTES.PRE_CLAIMS} />
          <Route exact path={APP_ROUTES.LOSS_AND_DAMAGE_CLAIMS} />
          <Route exact path={APP_ROUTES.OVERCHARGE_CLAIMS} />
          <Route exact path={APP_ROUTES.CLAIMS_SETTINGS} />
          <Route path="*" element={<LazyTranslatedPage element={PageNotFound} />} />
        </Route>

        <Route exact path={APP_ROUTES.CREATE_LOSS_AND_DAMAGE_CLAIMS} element={<LossAndDamageCreateClaimScreen />} />
        <Route exact path={APP_ROUTES.OVERCHARGE_CLAIMS_CREATE} element={<CreateOverChargeClaimsScreen />} />

        <Route />
        <Route exact path={APP_ROUTES.QUOTE_DETAILS} element={<LazyTranslatedPage element={QuoteDetails} />} />
        <Route
          exact
          path={APP_ROUTES.HAL_ROUTING_GUIDE}
          element={<LazyTranslatedPage element={HalRoutingGuidePage} />}
        />

        <Route exact path="/savers-home" element={<LazyTranslatedPage element={SaversHome} path="/savers-home" />} />

        <Route
          exact
          path="/trailer-request"
          element={<LazyTranslatedPage element={TrailerRequest} path="/trailer-request" />}
        />
        <Route
          exact
          path="/trailer-request-management"
          element={<LazyTranslatedPage element={TrailerMainPage} path="/trailer-request-management" />}
        />
        <Route
          exact
          path="/trailer-review"
          element={<LazyTranslatedPage element={TrailerReview} path="/trailer-review" />}
        />

        <Route exact path="/locations" element={<LazyTranslatedPage element={Locations} />} />

        <Route exact path="/trailer-database" element={<LazyTranslatedPage element={TrailerDatabase} />} />

        <Route exact path="/Supply-Zone" element={<LazyTranslatedPage element={SupplyZone} />} />

        <Route path="*" element={<LazyTranslatedPage element={PageNotFound} />} />
      </Route>

      <Route path={APP_ROUTES.ERROR} element={<LazyTranslatedPage element={ErrorPage} />} />
    </Routes>
  );
};

export default AppRoutes;
